/**
 * Turning "which devices does this person have access to" into table rows.
 *
 * The RFID panel showed `is_synced` — the app's own belief about whether it has written the
 * credential. That is not the same question as "is the credential actually on the reader right
 * now", which is what `device_credential_states` records and what the operator standing at a gate
 * needs. The two disagree exactly when it matters: a write that was attempted and never confirmed
 * reads as synced.
 *
 * So the row's state comes from the PERSON's own credential state on that device when there is
 * one, and falls back to the sync flag only when the ledger has nothing to say.
 */

import { mayStillHoldCredential } from '../../../utils/credentialPresence';
import { classifyDeviceSyncError, humanizeDeviceSyncError } from '../../../utils/deviceSyncError';

export type DeviceAccessTone = 'green' | 'amber' | 'red' | 'muted';

export interface DeviceAccessRow {
  deviceId: string;
  deviceName: string;
  /** Reader type, or the zone — whatever the table's second line is. */
  detail: string;
  stateLabel: string;
  tone: DeviceAccessTone;
  /** Device-reported failure text, surfaced as a tooltip rather than swallowed. */
  errorMessage?: string | null;
  /**
   * The person no longer has access to this device, but the ledger says the credential is
   * still on it. Nothing else in the UI reports this: the device is gone from
   * effective-access-zones, so it simply stopped being listed — which is precisely the
   * state someone needs to see, because the person can still open that gate.
   */
  orphan?: boolean;
}

export interface CredentialStateLike {
  device_id: string;
  device_name?: string | null;
  state: string;
  credential_type?: string | null;
  last_error_message?: string | null;
}

export interface AccessDeviceLike {
  id: string;
  name: string;
  device_type?: string | null;
  is_synced?: boolean;
}

/** The ledger's vocabulary, in the operator's. */
export function credentialStateLabel(state: string | null | undefined): { label: string; tone: DeviceAccessTone } {
  switch (state) {
    case 'confirmed_present':
      return { label: 'Confirmado no dispositivo', tone: 'green' };
    case 'confirmed_absent':
      return { label: 'Removido do dispositivo', tone: 'muted' };
    case 'grant_failed':
    case 'revoke_failed':
      return { label: 'Falha no dispositivo', tone: 'red' };
    case 'needs_reconciliation':
      return { label: 'Requer verificação', tone: 'red' };
    case 'grant_pending':
    case 'revoke_pending':
      return { label: 'Aguardando confirmação', tone: 'amber' };
    default:
      return { label: 'Não confirmado', tone: 'amber' };
  }
}

/**
 * Which of a device's credential rows speaks for the row in the table.
 *
 * A device holds one ledger row PER credential type (face, rfid_card, …), so a facial
 * terminal that also reads cards has several. Taking whichever the API returned first made
 * the displayed state depend on response ordering — the same reader could read
 * "Confirmado" or "Removido" between two loads with nothing changed on the device.
 *
 * Order of interest to an operator: something broken outranks something working, because
 * that is the row they have to act on; a confirmed credential outranks a pending one; and
 * anything at all outranks a plain absence.
 */
const STATE_PRIORITY: Record<string, number> = {
  needs_reconciliation: 5,
  grant_failed: 5,
  revoke_failed: 5,
  confirmed_present: 4,
  grant_pending: 3,
  revoke_pending: 3,
  confirmed_absent: 2,
  unknown: 1,
};

const statePriority = (state?: string | null): number => STATE_PRIORITY[state ?? ''] ?? 0;

/** Label for a credential stranded on a device the person no longer has access to. */
function orphanLabel(state: string | null | undefined): string {
  if (state === 'revoke_failed' || state === 'revoke_pending') {
    return 'Falha ao remover — ainda no dispositivo';
  }
  return 'Acesso removido — ainda no dispositivo';
}

/**
 * A refused PHOTO is not a broken reader.
 *
 * "Falha no dispositivo" on both catracas reads as "the person is not on these readers" —
 * but when the device accepted the user record and only rejected the photo, the person IS
 * enrolled and the fix is a new photo, not a retry. The cause is in last_error_message, so
 * the label follows the classified cause: a FACE-* code gets the photo-specific wording,
 * anything else (network, auth) keeps the generic label, because for those the photo never
 * even arrived.
 */
function failureLabel(state: CredentialStateLike): { label: string; tone: DeviceAccessTone } {
  const raw = state.last_error_message;
  if (raw) {
    const { code, title } = classifyDeviceSyncError(raw);
    if (code.startsWith('FACE-')) return { label: title, tone: 'red' };
  }
  return { label: 'Falha no dispositivo', tone: 'red' };
}

/** The recorded cause, in the operator's language — never the raw vendor string. */
function readableError(raw: string | null | undefined): string | null {
  return raw ? humanizeDeviceSyncError(raw) : null;
}

export function buildDeviceAccessRows(
  devices: AccessDeviceLike[],
  states: CredentialStateLike[],
  detailFor: (device: AccessDeviceLike) => string,
): DeviceAccessRow[] {
  const byDevice = new Map<string, CredentialStateLike>();
  (states || []).forEach((s) => {
    if (!s || !s.device_id) return;
    const current = byDevice.get(s.device_id);
    if (!current || statePriority(s.state) > statePriority(current.state)) {
      byDevice.set(s.device_id, s);
    }
  });

  const rows: DeviceAccessRow[] = (devices || []).map((device): DeviceAccessRow => {
    const state = byDevice.get(device.id);
    if (state) {
      const isFailure = state.state === 'grant_failed' || state.state === 'revoke_failed';
      const { label, tone } = isFailure ? failureLabel(state) : credentialStateLabel(state.state);
      return {
        deviceId: device.id,
        deviceName: device.name,
        detail: detailFor(device),
        stateLabel: label,
        tone,
        errorMessage: readableError(state.last_error_message),
      };
    }

    // No ledger entry for this person on this device: the app's sync flag is all we have, and it
    // is a weaker claim — say so rather than dressing it up as a confirmation.
    return {
      deviceId: device.id,
      deviceName: device.name,
      detail: detailFor(device),
      stateLabel: device.is_synced ? 'Enviado, não confirmado' : 'Sem credencial',
      tone: device.is_synced ? 'amber' : 'muted',
      errorMessage: null,
    };
  });

  // Anything the ledger knows about that is NOT in the person's current zones: a credential
  // outliving the access that justified it. These lead the table — they are the only rows
  // that ask for an action.
  const inZones = new Set((devices || []).map((d) => d.id));
  const orphans: DeviceAccessRow[] = [];
  byDevice.forEach((state, deviceId) => {
    if (inZones.has(deviceId) || !mayStillHoldCredential(state.state)) return;
    orphans.push({
      deviceId,
      deviceName: state.device_name || deviceId,
      detail: 'Fora das zonas de acesso atuais',
      stateLabel: orphanLabel(state.state),
      tone: 'red',
      errorMessage: readableError(state.last_error_message),
      orphan: true,
    });
  });

  return [...orphans, ...rows];
}
